import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MAX_CONTENT_WIDTH } from '../constants/layout';

const PricingPage = () => {
  const [billingPeriod, setBillingPeriod] = useState('yearly');

  // Calculate monthly price from yearly price
  const getMonthlyFromYearly = (yearlyPrice) => {
    const yearly = parseFloat(yearlyPrice.replace(/[$,]/g, ''));
    const monthly = yearly / 12;
    return `$${Math.floor(monthly)}`;
  };

  // Reusable features list
  const FEATURES = {
    DASHBOARD: 'Your Dashboard',
    PRODUCT_MANAGEMENT: 'Product Management',
    PHOTOS: 'Photos',
    VIDEOS: 'Videos',
    ONLINE_ORDERING: 'Accept payments',
    INVENTORY: 'Inventory',
    USER_AUTH: 'User Authentication',
    PUSH_NOTIFICATIONS: 'Push Notifications (Send alerts to users)',
    IOS_APP: 'iPhone/iPad App',
    ANDROID_APP: 'Android App',
  };

  const pricingTiers = [
    {
      name: 'Website (Basic)',
      monthlyPrice: '$35',
      yearlyPrice: '$350',
      yearlySavings: 'Save $70',
      bestFor: 'Getting discovered online and showcasing your work',
      features: {
        [FEATURES.DASHBOARD]: true,
        [FEATURES.PRODUCT_MANAGEMENT]: true,
        [FEATURES.PHOTOS]: true,
        [FEATURES.VIDEOS]: false,
        [FEATURES.ONLINE_ORDERING]: false,
        [FEATURES.INVENTORY]: false,
        [FEATURES.USER_AUTH]: false,
        [FEATURES.PUSH_NOTIFICATIONS]: false,
        [FEATURES.IOS_APP]: false,
        [FEATURES.ANDROID_APP]: false,
      }
    },
    {
      name: 'Website (Online Store)',
      monthlyPrice: '$65',
      yearlyPrice: '$650',
      yearlySavings: 'Save $200',
      featured: true,
      bestFor: 'Taking orders and managing inventory online',
      features: {
        [FEATURES.DASHBOARD]: true,
        [FEATURES.PRODUCT_MANAGEMENT]: true,
        [FEATURES.PHOTOS]: true,
        [FEATURES.VIDEOS]: true,
        [FEATURES.ONLINE_ORDERING]: true,
        [FEATURES.INVENTORY]: true,
        [FEATURES.USER_AUTH]: true,
        [FEATURES.PUSH_NOTIFICATIONS]: false,
        [FEATURES.IOS_APP]: false,
        [FEATURES.ANDROID_APP]: false,
      }
    },
    {
      name: 'Website + iOS App',
      monthlyPrice: '$145',
      yearlyPrice: '$1,450',
      yearlySavings: 'Save $290',
      bestFor: 'Engaging customers with an iPhone and iPad app and notifications',
      features: {
        [FEATURES.DASHBOARD]: true,
        [FEATURES.PRODUCT_MANAGEMENT]: true,
        [FEATURES.PHOTOS]: true,
        [FEATURES.VIDEOS]: true,
        [FEATURES.ONLINE_ORDERING]: true,
        [FEATURES.INVENTORY]: true,
        [FEATURES.USER_AUTH]: true,
        [FEATURES.PUSH_NOTIFICATIONS]: true,
        [FEATURES.IOS_APP]: true,
        [FEATURES.ANDROID_APP]: false,
      }
    },
    {
      name: 'Website + iOS App + Android App',
      monthlyPrice: '$200',
      yearlyPrice: '$2,000',
      yearlySavings: 'Save $400',
      bestFor: 'Reaching customers on any device, anywhere',
      features: {
        [FEATURES.DASHBOARD]: true,
        [FEATURES.PRODUCT_MANAGEMENT]: true,
        [FEATURES.PHOTOS]: true,
        [FEATURES.VIDEOS]: true,
        [FEATURES.ONLINE_ORDERING]: true,
        [FEATURES.INVENTORY]: true,
        [FEATURES.USER_AUTH]: true,
        [FEATURES.PUSH_NOTIFICATIONS]: true,
        [FEATURES.IOS_APP]: true,
        [FEATURES.ANDROID_APP]: true,
      }
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--color-background)',
      paddingTop: '0',
      paddingBottom: '60px'
    }}>
      {/* Header - Constrained */}
      <div style={{
        maxWidth: MAX_CONTENT_WIDTH,
        margin: '0 auto',
        padding: '0 20px'
      }}>
        <div style={{
          textAlign: 'center',
          marginBottom: '60px'
        }}>
          <h1 style={{
            color: 'var(--color-text-primary)',
            fontSize: '40px',
            fontWeight: '700',
            marginBottom: '16px',
            letterSpacing: '-0.05em',
            fontFamily: 'Helvetica, Arial, sans-serif'
          }}>
            AppStack for Business
          </h1>
          <p style={{
            color: 'var(--color-text-secondary)',
            fontSize: '16px',
            lineHeight: '1.6',
            maxWidth: '600px',
            margin: '0 auto 16px',
            letterSpacing: '-0.02em',
            fontFamily: 'Helvetica, Arial, sans-serif'
          }}>
            Choose the perfect plan for your business. All plans include hosting, updates, and a backend console.
          </p>
          <p style={{
            color: 'var(--color-accent)',
            fontSize: '16px',
            fontWeight: '600',
            maxWidth: '600px',
            margin: '0 auto',
            letterSpacing: '-0.02em',
            fontFamily: 'Helvetica, Arial, sans-serif'
          }}>
            Transparent pricing. No quotes needed. Cancel anytime.
          </p>
        </div>

        {/* Billing Segmented Picker */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '40px'
        }}>
          <div style={{
            display: 'inline-flex',
            backgroundColor: 'var(--color-surface-muted)',
            border: '1px solid var(--color-border)',
            borderRadius: '10px',
            padding: '4px',
            gap: '4px'
          }}>
            <button
              onClick={() => setBillingPeriod('monthly')}
              style={{
                padding: '10px 24px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: billingPeriod === 'monthly' ? 'var(--color-surface)' : 'transparent',
                color: billingPeriod === 'monthly' ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease, color 0.2s ease',
                fontFamily: 'Helvetica, Arial, sans-serif',
                boxShadow: billingPeriod === 'monthly' ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none',
              }}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              style={{
                padding: '10px 24px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: billingPeriod === 'yearly' ? 'var(--color-surface)' : 'transparent',
                color: billingPeriod === 'yearly' ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease, color 0.2s ease',
                fontFamily: 'Helvetica, Arial, sans-serif',
                boxShadow: billingPeriod === 'yearly' ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none',
              }}
            >
              Yearly
            </button>
          </div>
        </div>
      </div>

      {/* Pricing Cards - Full Width */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '60px'
      }}>
        <div style={{
          display: 'flex',
          gap: '24px',
          overflowX: 'auto',
          paddingLeft: '20px',
          paddingRight: '20px',
          paddingTop: '20px',
          paddingBottom: '40px',
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          maxWidth: '100%',
          width: 'fit-content'
        }}>
          {pricingTiers.map((tier, index) => (
            <div
              key={index}
              style={{
                backgroundColor: 'var(--color-surface)',
                borderRadius: '16px',
                padding: '32px 24px',
                border: tier.featured ? '2px solid var(--color-accent)' : '1px solid var(--color-border)',
                position: 'relative',
                transition: 'transform 0.2s ease, border-color 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                width: '320px',
                flexShrink: 0,
                scrollSnapAlign: 'start'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {/* {tier.featured && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: 'var(--color-accent)',
                  color: 'white',
                  padding: '4px 16px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Most Popular
                </div>
              )} */}

              <h3 style={{
                color: 'var(--color-text-primary)',
                fontSize: '24px',
                fontWeight: '700',
                marginBottom: '12px',
                marginTop: '0',
                letterSpacing: '-0.03em',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}>
                {tier.name}
              </h3>

              <div style={{
                marginBottom: '24px'
              }}>
                <div style={{
                  fontSize: '48px',
                  fontWeight: '700',
                  color: 'var(--color-text-primary)',
                  lineHeight: '1',
                  letterSpacing: '-0.04em',
                  fontFamily: 'Helvetica, Arial, sans-serif'
                }}>
                  {billingPeriod === 'monthly' ? tier.monthlyPrice : getMonthlyFromYearly(tier.yearlyPrice)}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: 'var(--color-text-secondary)',
                  marginTop: '8px'
                }}>
                  {billingPeriod === 'monthly' ? '/month' : `/month (${tier.yearlySavings} yearly)`}
                </div>
              </div>

              <Link
                to="/business/get-started"
                style={{
                  backgroundColor: tier.featured ? 'var(--color-accent)' : 'var(--color-surface-muted)',
                  color: tier.featured ? 'white' : 'var(--color-text-primary)',
                  border: tier.featured ? 'none' : '1px solid var(--color-border)',
                  borderRadius: '8px',
                  padding: '14px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '32px',
                  textDecoration: 'none',
                  display: 'block',
                  textAlign: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.02)';
                  e.target.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.opacity = '1';
                }}
              >
                Get Started
              </Link>

              <div style={{
                borderTop: '1px solid var(--color-border)',
                paddingTop: '24px',
                marginBottom: '24px'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Best For
                </div>
                <p style={{
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: 'var(--color-text-primary)',
                  margin: 0,
                  fontFamily: 'Helvetica, Arial, sans-serif'
                }}>
                  {tier.bestFor}
                </p>
              </div>

              <div style={{
                borderTop: '1px solid var(--color-border)',
                paddingTop: '24px'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Features
                </div>
                {Object.entries(tier.features).map(([feature, included]) => (
                  <div
                    key={feature}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      marginBottom: '12px',
                      color: included ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                      opacity: included ? 1 : 0.5
                    }}
                  >
                    <span style={{
                      fontSize: '18px',
                      lineHeight: '1',
                      flexShrink: 0,
                      marginTop: '2px'
                    }}>
                      {included ? '✓' : '—'}
                    </span>
                    <span style={{
                      fontSize: '14px',
                      lineHeight: '1.5'
                    }}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing Note */}
      <div style={{
        textAlign: 'center',
        marginBottom: '60px',
        padding: '0 20px'
      }}>
        <p style={{
          fontSize: '12px',
          color: 'var(--color-text-secondary)',
          fontWeight: '600',
          letterSpacing: '0.05em',
          margin: 0,
          fontFamily: 'Helvetica, Arial, sans-serif',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '16px' }}>🇺🇸</span>
          ALL PRICES IN US DOLLARS
        </p>
      </div>

      {/* FAQ Section - Constrained */}
      <div style={{
        maxWidth: MAX_CONTENT_WIDTH,
        margin: '0 auto',
        padding: '0 20px'
      }}>
        <div style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '16px',
          padding: '40px',
          marginTop: '60px'
        }}>
          <h2 style={{
            color: 'var(--color-text-primary)',
            fontSize: '32px',
            fontWeight: '700',
            marginBottom: '32px',
            textAlign: 'center',
            letterSpacing: '-0.04em',
            fontFamily: 'Helvetica, Arial, sans-serif'
          }}>
            Frequently Asked Questions
          </h2>

          <div style={{
            display: 'grid',
            gap: '24px',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <div>
              <h3 style={{
                color: 'var(--color-text-primary)',
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '8px',
                letterSpacing: '-0.02em',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}>
                Can I cancel anytime?
              </h3>
              <p style={{
                color: 'var(--color-text-secondary)',
                fontSize: '16px',
                lineHeight: '1.6',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}>
                Yes! All plans can be cancelled anytime with no additional fees or penalties.
              </p>
            </div>

            <div>
              <h3 style={{
                color: 'var(--color-text-primary)',
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '8px',
                letterSpacing: '-0.02em',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}>
                What's included in your dashboard?
              </h3>
              <p style={{
                color: 'var(--color-text-secondary)',
                fontSize: '16px',
                lineHeight: '1.6',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}>
                Your dashboard gives you full control over your content, products, and settings. You can manage everything from a user-friendly interface.
              </p>
            </div>

            <div>
              <h3 style={{
                color: 'var(--color-text-primary)',
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '8px',
                letterSpacing: '-0.02em',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}>
                Can I upgrade or downgrade my plan?
              </h3>
              <p style={{
                color: 'var(--color-text-secondary)',
                fontSize: '16px',
                lineHeight: '1.6',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}>
                Absolutely! You can upgrade or downgrade your plan at any time to match your evolving business needs.
              </p>
            </div>

            <div>
              <h3 style={{
                color: 'var(--color-text-primary)',
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '8px',
                letterSpacing: '-0.02em',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}>
                Do iOS apps work on both iPhone and iPad?
              </h3>
              <p style={{
                color: 'var(--color-text-secondary)',
                fontSize: '16px',
                lineHeight: '1.6',
                fontFamily: 'Helvetica, Arial, sans-serif'
              }}>
                Yes! The iOS app tier includes native apps for both iPhone and iPad with optimized interfaces for each device.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
